import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

export default function TypingIndicator() {
  // Array of cosmic-themed messages
  const cosmicMessages = [
    "Exploring the cosmos for answers...",
    "Navigating through the stars...",
    "Consulting the cosmic intelligence...",
    "Searching across the universe...",
    "Gathering wisdom from distant galaxies...",
    "Calculating quantum probabilities...",
    "Decoding stellar patterns...",
    "Traversing the cosmic web...",
    "Analyzing celestial data...",
    "Connecting to the cosmic network...",
  ];

  // State to hold the current message
  const [message, setMessage] = useState(cosmicMessages[0]);

  // Effect to change the message periodically (optional)
  useEffect(() => {
    // Change message every 8 seconds
    const intervalId = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * cosmicMessages.length);
      setMessage(cosmicMessages[randomIndex]);
    }, 8000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="flex items-start !mt-2 mb-4">
      <div className="rounded-lg p-4 bg-gradient-to-br from-black to-gray-800 backdrop-blur-sm border border-gray-700 text-white max-w-[80%] relative overflow-hidden">
        {/* Animated stars in background */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(10)].map((_, i) => (
            <span
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                opacity: Math.random() * 0.7 + 0.3,
                backgroundColor: "white",
                borderRadius: "50%",
                animation: `twinkle ${
                  Math.random() * 3 + 2
                }s infinite alternate`,
              }}
            />
          ))}

          {/* Nebula effect */}
          <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-tr from-gray-700/20 to-gray-600/10 blur-xl"></div>
          <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-gradient-to-br from-gray-700/20 to-gray-600/10 blur-xl"></div>
        </div>

        <div className="flex items-center relative z-10">
          {/* Cosmic brain animation */}
          <div className="relative mr-3">
            <motion.div
              className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center"
              animate={{
                boxShadow: [
                  "0 0 0 rgba(75, 85, 99, 0.4)",
                  "0 0 8px rgba(75, 85, 99, 0.6)",
                  "0 0 0 rgba(75, 85, 99, 0.4)",
                ],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              {/* Orbital rings */}
              <motion.div
                className="absolute w-full h-full rounded-full border border-gray-400/30"
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              />
              <motion.div
                className="absolute w-[140%] h-[70%] rounded-full border border-gray-400/20"
                animate={{ rotate: -360 }}
                transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                style={{ transformOrigin: "center" }}
              />

              {/* Pulsing core */}
              <motion.div
                className="w-4 h-4 rounded-full bg-gradient-to-r from-gray-400 to-gray-600"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.7, 1, 0.7],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* Particles */}
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full"
                  initial={{
                    x: 0,
                    y: 0,
                  }}
                  animate={{
                    x: [0, (Math.random() - 0.5) * 10],
                    y: [0, (Math.random() - 0.5) * 10],
                    opacity: [1, 0],
                    scale: [1, 0],
                  }}
                  transition={{
                    duration: 1 + Math.random(),
                    repeat: Infinity,
                    repeatDelay: Math.random() * 0.5,
                  }}
                />
              ))}
            </motion.div>
          </div>

          <div className="flex flex-col">
            <div className="flex items-center">
              <div className="flex space-x-1 mr-2">
                <motion.div
                  className="w-2 h-2 bg-gray-300 rounded-full"
                  animate={{ y: [0, -4, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0,
                  }}
                />
                <motion.div
                  className="w-2 h-2 bg-gray-300 rounded-full"
                  animate={{ y: [0, -4, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.2,
                  }}
                />
                <motion.div
                  className="w-2 h-2 bg-gray-300 rounded-full"
                  animate={{ y: [0, -4, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.4,
                  }}
                />
              </div>
              <span className="text-sm font-medium text-white">Thinking</span>
            </div>
            <span className="text-xs text-gray-300 mt-0.5">{message}</span>
          </div>
        </div>

        {/* Animated progress bar */}
        <motion.div
          className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-gray-500 via-white to-gray-500"
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
    </div>
  );
}
